import {
    Badge,
    Code,
    CopyButton,
    Drawer,
    Group,
    Loader,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminCityPurchaseDetailRO } from '@routellers/types/dist/openapi/city-payment/admin-city-purchase.ro';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { EntityThumb } from '../components/entity-thumb';
import { Section } from '../components/editor/section';
import { formatDateTime, formatMoneyCents } from '../utils/portal';

type Props = {
    opened: boolean;
    purchaseId: number | null;
    onClose: () => void;
};

const Copyable = ({ value }: { value: string }) => (
    <Group gap={6} wrap='nowrap'>
        <Code style={{ wordBreak: 'break-all' }}>{value}</Code>
        <CopyButton value={value}>
            {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                    <UnstyledButton onClick={copy} style={{ display: 'flex' }}>
                        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </UnstyledButton>
                </Tooltip>
            )}
        </CopyButton>
    </Group>
);

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Group justify='space-between' align='flex-start' gap='md' wrap='nowrap'>
        <Text size='sm' c='dimmed' style={{ flexShrink: 0, minWidth: 120 }}>
            {label}
        </Text>
        <Stack gap={0} align='flex-end' style={{ minWidth: 0, flex: 1 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Text size='sm' ta='right' style={{ wordBreak: 'break-word' }}>
                    {children}
                </Text>
            ) : (
                children
            )}
        </Stack>
    </Group>
);

export const PurchaseDetailDrawer = ({ opened, purchaseId, onClose }: Props) => {
    const { t } = useTranslation();
    const [detail, setDetail] = useState<AdminCityPurchaseDetailRO | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!opened || purchaseId == null) {
            setDetail(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setDetail(null);

        api.cityPurchasesAdminApi
            .getOne(purchaseId)
            .then(({ data }) => {
                if (!cancelled) setDetail(data);
            })
            .catch((error) => {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    onClose();
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [opened, purchaseId]);

    const entitlementStatus = (() => {
        if (!detail?.entitlement) return null;
        if (detail.entitlement.revokedAt) return 'revoked';
        if (
            detail.entitlement.expiresAt &&
            new Date(detail.entitlement.expiresAt).getTime() < Date.now()
        ) {
            return 'expired';
        }
        return 'active';
    })();

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={
                detail
                    ? t('pages.purchases.detailTitle', { id: detail.id })
                    : t('pages.purchases.detailFallback')
            }
            position='right'
            size='lg'
            padding='md'
        >
            {loading || !detail ? (
                <Loader />
            ) : (
                <Stack gap='lg'>
                    <Group gap='sm'>
                        <Badge
                            color={detail.provider === 'stripe' ? 'violet' : 'blue'}
                            variant='light'
                        >
                            {t(`enums.purchaseProvider.${detail.provider}`)}
                        </Badge>
                        {detail.refundedAt ? (
                            <Badge color='red' variant='light'>
                                {t('pages.purchases.status.refunded')}
                            </Badge>
                        ) : (
                            <Badge color='green' variant='light'>
                                {t('pages.purchases.status.paid')}
                            </Badge>
                        )}
                        <Text size='sm' c='dimmed'>
                            {formatDateTime(detail.createdAt)}
                        </Text>
                    </Group>

                    <Section title={t('pages.purchases.sections.product')} boxed>
                        <EntityThumb
                            src={detail.product.coverUrl}
                            name={detail.product.title}
                            subtitle={
                                detail.product.cityTitle ||
                                (detail.product.cityId != null
                                    ? `City #${detail.product.cityId}`
                                    : t(`enums.purchaseScope.${detail.scope}`))
                            }
                            radius='md'
                        />
                        <Stack gap={6} mt='sm'>
                            <Row label={t('pages.purchases.fields.scope')}>
                                {t(`enums.purchaseScope.${detail.scope}`)}
                            </Row>
                            {detail.scopeId != null && (
                                <Row label={t('pages.purchases.fields.scopeId')}>
                                    {detail.scopeId}
                                </Row>
                            )}
                            {detail.product.cityTitle && (
                                <Row label={t('pages.purchases.fields.city')}>
                                    {detail.product.cityTitle}
                                </Row>
                            )}
                        </Stack>
                    </Section>

                    <Section title={t('pages.purchases.sections.buyer')} boxed>
                        <Stack gap={6}>
                            <Row label={t('pages.purchases.fields.email')}>
                                {detail.player.email}
                            </Row>
                            {detail.player.fullName && (
                                <Row label={t('pages.purchases.fields.name')}>
                                    {detail.player.fullName}
                                </Row>
                            )}
                            <Row label={t('pages.purchases.fields.playerId')}>
                                {detail.player.id}
                            </Row>
                            <Row label={t('pages.purchases.fields.playerSince')}>
                                {formatDateTime(detail.player.createdAt)}
                            </Row>
                            <Row label={t('pages.purchases.fields.lastLogin')}>
                                {detail.player.lastLoginAt
                                    ? formatDateTime(detail.player.lastLoginAt)
                                    : '—'}
                            </Row>
                            <Row label={t('pages.purchases.fields.emailVerified')}>
                                {detail.player.emailVerifiedAt
                                    ? formatDateTime(detail.player.emailVerifiedAt)
                                    : '—'}
                            </Row>
                            <Row label={t('pages.purchases.fields.playerPurchases')}>
                                {detail.playerPurchaseCount}
                            </Row>
                        </Stack>
                    </Section>

                    <Section title={t('pages.purchases.sections.payment')} boxed>
                        <Stack gap={6}>
                            <Row label={t('pages.purchases.fields.amount')}>
                                {formatMoneyCents(detail.amountPaidCents, detail.currency)}
                            </Row>
                            <Row label={t('pages.purchases.fields.tax')}>
                                {formatMoneyCents(detail.taxCents, detail.currency)}
                            </Row>
                            <Row label={t('pages.purchases.fields.provider')}>
                                {t(`enums.purchaseProvider.${detail.provider}`)}
                            </Row>
                            <Row label={t('pages.purchases.fields.externalId')}>
                                <Copyable value={detail.externalId} />
                            </Row>
                            {detail.refundedAt && (
                                <Row label={t('pages.purchases.fields.refundedAt')}>
                                    {formatDateTime(detail.refundedAt)}
                                </Row>
                            )}
                            <Row label={t('pages.purchases.fields.createdAt')}>
                                {formatDateTime(detail.createdAt)}
                            </Row>
                        </Stack>
                    </Section>

                    <Section title={t('pages.purchases.sections.entitlement')} boxed>
                        {!detail.entitlement ? (
                            <Text size='sm' c='dimmed'>
                                {t('pages.purchases.noEntitlement')}
                            </Text>
                        ) : (
                            <Stack gap={6}>
                                <Row label={t('pages.purchases.fields.status')}>
                                    <Badge
                                        color={
                                            entitlementStatus === 'active'
                                                ? 'green'
                                                : entitlementStatus === 'expired'
                                                  ? 'yellow'
                                                  : 'red'
                                        }
                                        variant='light'
                                    >
                                        {t(`pages.purchases.entitlementStatus.${entitlementStatus}`)}
                                    </Badge>
                                </Row>
                                <Row label={t('pages.purchases.fields.entitlementId')}>
                                    {detail.entitlement.id}
                                </Row>
                                <Row label={t('pages.purchases.fields.source')}>
                                    {detail.entitlement.source}
                                </Row>
                                <Row label={t('pages.purchases.fields.expiresAt')}>
                                    {detail.entitlement.expiresAt
                                        ? formatDateTime(detail.entitlement.expiresAt)
                                        : t('pages.purchases.permanent')}
                                </Row>
                                {detail.entitlement.revokedAt && (
                                    <Row label={t('pages.purchases.fields.revokedAt')}>
                                        {formatDateTime(detail.entitlement.revokedAt)}
                                    </Row>
                                )}
                                {detail.entitlement.storeTransactionId && (
                                    <Row label={t('pages.purchases.fields.storeTxn')}>
                                        <Copyable value={detail.entitlement.storeTransactionId} />
                                    </Row>
                                )}
                            </Stack>
                        )}
                    </Section>

                    <Section title={t('pages.purchases.sections.attribution')} boxed>
                        {!detail.partner && !detail.promoClaim ? (
                            <Text size='sm' c='dimmed'>
                                {t('pages.purchases.noAttribution')}
                            </Text>
                        ) : (
                            <Stack gap={6}>
                                {detail.partner && (
                                    <>
                                        <Row label={t('pages.purchases.fields.venue')}>
                                            {detail.partner.companyName || detail.partner.name}
                                        </Row>
                                        {detail.partner.email && (
                                            <Row label={t('pages.purchases.fields.venueEmail')}>
                                                {detail.partner.email}
                                            </Row>
                                        )}
                                        {detail.partner.promocode && (
                                            <Row label={t('pages.purchases.fields.promocode')}>
                                                {detail.partner.promocode}
                                            </Row>
                                        )}
                                    </>
                                )}
                                {detail.promoClaim && (
                                    <>
                                        <Row label={t('pages.purchases.fields.claimCode')}>
                                            {detail.promoClaim.code}
                                        </Row>
                                        <Row label={t('pages.purchases.fields.discount')}>
                                            {detail.promoClaim.discountPercent}%
                                        </Row>
                                        <Row label={t('pages.purchases.fields.claimAttributed')}>
                                            {detail.promoClaim.attributedPurchaseCount}
                                        </Row>
                                    </>
                                )}
                                {detail.commissionCents != null && (
                                    <Row label={t('pages.purchases.fields.commission')}>
                                        {formatMoneyCents(
                                            detail.commissionCents,
                                            detail.currency,
                                        )}
                                        {detail.commissionPercent != null
                                            ? ` (${detail.commissionPercent}%)`
                                            : ''}
                                    </Row>
                                )}
                                {detail.commission && (
                                    <Row label={t('pages.purchases.fields.commissionStatus')}>
                                        {detail.commission.status}
                                        {detail.commission.payoutId != null
                                            ? ` · payout #${detail.commission.payoutId}`
                                            : ''}
                                    </Row>
                                )}
                            </Stack>
                        )}
                    </Section>

                    <Section title={t('pages.purchases.sections.consent')} boxed>
                        {!detail.consent ? (
                            <Text size='sm' c='dimmed'>
                                {t('pages.purchases.noConsent')}
                            </Text>
                        ) : (
                            <Stack gap={6}>
                                <Row label={t('pages.purchases.fields.consentEmail')}>
                                    {detail.consent.email}
                                </Row>
                                <Row label={t('pages.purchases.fields.docsVersion')}>
                                    {detail.consent.docsVersion}
                                </Row>
                                <Row label={t('pages.purchases.fields.locale')}>
                                    {detail.consent.locale || '—'}
                                </Row>
                                <Row label={t('pages.purchases.fields.consentAt')}>
                                    {formatDateTime(detail.consent.createdAt)}
                                </Row>
                            </Stack>
                        )}
                    </Section>
                </Stack>
            )}
        </Drawer>
    );
};
